// ============================================================================
// ui/fly-to-switcher.js — a ghost of the product art flies to the switcher bar
// ============================================================================
// Ryan 2026-08-09: *"when you click on a + or on a product button to add it to your selection, the
// product image has a ghost version fly to the selector button/bar… So basically people can see
// that what they're adding is in fact being added, and then they get educated on that bar and
// realize they can interact with it to see the new weight they just added, as opposed to thinking
// 'I just added a weight, how come I don't see it on the screen'."*
//
// ⭐ THE PROBLEM IS REAL AND THE OBVIOUS FIX IS WORSE. Adding a product deliberately does NOT move
// the stage — `pickedProducts()` keeps the current product if it survived the edit — so nothing on
// screen acknowledges the add. Switching the stage to whatever was just added would answer that and
// cost more than it gives: picking three products in a row would yank the view three times and lose
// the buyer's place. This teaches where the new product went instead of hijacking the screen.
//
// ⚠ NOTHING FLIES ON THE FIRST PICK, and that is not a special case so much as the shape of the
// feature. `product-down-tab` sets `el.hidden = items.length < 2`, so there is no bar to fly to
// until the second product — and no confusion to solve either, because pick #1 mounts on the stage.
// From the second add on, the bar appears in the same beat, so the ghost doubles as its
// introduction rather than pointing at furniture the buyer already knows.
//
// ⛔ THE GHOST IS APPENDED TO <body>. Born inside the accordion it would be clipped at the pane's
// edge on desktop and the sheet's on a phone — both are `overflow: hidden` — so the flight would
// end a few pixels from where it started. It carries no inherited styling, so the usual cost of
// hoisting (memory `hoisting-to-body-loses-scoped-css-vars`) does not apply here.
//
// ⚠ TRANSFORM AND OPACITY ONLY. This plays over the live 3D canvas, which is exactly where this app
// has historically stuttered (project CLAUDE.md, on stacking glass over the stage). Those two are
// compositor-only, so one small image in flight costs nothing the renderer can feel.
// ============================================================================

import { land } from './haptics.js';

const REDUCED = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
};

/* 530ms — 15% over the 460 it shipped at (Ryan 2026-08-09, having watched it). Long enough to be
   followed across a phone screen without becoming something you wait for. */
const DURATION = 530;

/**
 * Bump the destination so the arrival is felt even if the flight was missed — and so there is
 * still an acknowledgement under reduced motion, where the flight itself is skipped.
 */
function landOn(target) {
  if (!target) return;
  /* The arrival, felt as well as seen — the second half of the press/land pair the `+` starts. See
     the strength table in `ui/haptics.js` for why this one is lighter than the press that threw it. */
  land();
  target.classList.remove('wd-fly-land');
  // Force a reflow so re-adding the class restarts the animation on a rapid second add.
  void target.offsetWidth;
  target.classList.add('wd-fly-land');
  setTimeout(() => target.classList.remove('wd-fly-land'), 700);
}

/**
 * @param {HTMLElement} fromEl  the art being "taken" — the category tile on the Pick Weights card.
 * @param {HTMLElement} toEl    where it lands — the switcher bar's counter.
 */
export function flyToSwitcher(fromEl, toEl) {
  if (!fromEl || !toEl) return;
  /* ⚠ A HIDDEN BAR HAS A ZERO RECT, not a missing one. `el.hidden` on the tab means
     `getBoundingClientRect()` answers all zeros rather than throwing, so a flight to the
     first-pick bar would animate to the top-left corner of the screen instead of not running. */
  const a = fromEl.getBoundingClientRect();
  const b = toEl.getBoundingClientRect();
  if (!a.width || !a.height || !b.width || !b.height) return;

  if (REDUCED()) { landOn(toEl); return; }

  const img = fromEl.querySelector('img') || fromEl;
  const src = img.currentSrc || img.src;
  if (!src) { landOn(toEl); return; }

  const ghost = document.createElement('img');
  ghost.src = src;
  ghost.className = 'wd-fly-ghost';
  ghost.setAttribute('aria-hidden', 'true');
  ghost.style.left = `${a.left}px`;
  ghost.style.top = `${a.top}px`;
  ghost.style.width = `${a.width}px`;
  ghost.style.height = `${a.height}px`;
  document.body.appendChild(ghost);

  const dx = (b.left + b.width / 2) - (a.left + a.width / 2);
  const dy = (b.top + b.height / 2) - (a.top + a.height / 2);
  const end = Math.max(0.22, Math.min(b.width, b.height) / Math.max(a.width, a.height));

  /* ⚠ AN ARC, NOT A STRAIGHT LINE, and the lift is perpendicular-ish rather than always upward: on
     desktop the bar sits below the pane and on a phone it sits above it, so a fixed "up" would bow
     the wrong way on one of them. Signing the lift by the direction of travel keeps the curve
     reading as a throw on both. */
  const lift = (dy < 0 ? -1 : 1) * Math.min(90, Math.abs(dx) * 0.25 + 30);

  const anim = ghost.animate(
    [
      { transform: 'translate(0px, 0px) scale(1)', opacity: 0.95, offset: 0 },
      { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - lift}px) scale(${(1 + end) / 2})`, opacity: 0.9, offset: 0.55 },
      { transform: `translate(${dx}px, ${dy}px) scale(${end})`, opacity: 0, offset: 1 },
    ],
    { duration: DURATION, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' },
  );

  const done = () => ghost.remove();
  anim.finished.then(done, done);
  // Belt and braces: a tab backgrounded mid-flight can leave `finished` unsettled for a long time,
  // and a stray fixed-position image over the stage is worse than a missed animation.
  setTimeout(done, DURATION + 400);

  setTimeout(() => landOn(toEl), DURATION - 90);
}

/**
 * The opposite gesture: a product being REMOVED gets thrown away.
 *
 * Ryan 2026-08-09: *"the image pops up in the air and to the left then flys down the screen… like
 * it's getting thrown over the railing into the river or something.. plop!"*
 *
 * ⭐ IT IS THE SAME ARGUMENT AS THE FLY, RUN BACKWARDS. Adding a product changes nothing on the
 * stage, so the ghost says where it went; removing one changes nothing either, and a row quietly
 * vanishing from a list is the weakest possible confirmation that something left the ORDER. This
 * gives the removal a direction, and the direction is away.
 *
 * ⚠ TWO EASINGS, NOT ONE, because a single curve reads as a slide rather than a throw. The rise is
 * `ease-out` (a toss decelerating into its apex) and the fall is `ease-in` (gravity taking over),
 * which is the whole difference between "thrown over a railing" and "moved to the corner".
 * ⚠ IT LEAVES THE SCREEN rather than fading in place: the fall is measured to the viewport's bottom
 * plus a margin, so it is genuinely gone rather than dissolving where the buyer is still looking.
 */
export function tossAway(origin, src, size = 44) {
  /* ⚠ AN ORIGIN POINT, NOT AN ELEMENT, and that is forced by the ordering. The throw starts at the
     SWITCHER BAR (Ryan 2026-08-09: *"it should plop from the selection bar"*) — but by the time this
     runs the product has already been removed, so the bar has re-rendered and, on the 2→1 crossing,
     hidden itself entirely. The caller therefore measures the bar BEFORE the removal and hands the
     point over. Asking the DOM here would read a bar that no longer describes what was thrown.
     ⚠ NO BAR, NO THROW. Below two products there is no switcher, so a removal there has nothing to
     leave from — and the row vanishing is already the confirmation, exactly as under reduced
     motion. Mirrors the fly, which likewise only happens when there is a bar to fly INTO. */
  if (!origin || !src) return;
  if (REDUCED()) return;

  const ghost = document.createElement('img');
  ghost.src = src;
  ghost.className = 'wd-fly-ghost';
  ghost.setAttribute('aria-hidden', 'true');
  ghost.style.left = `${Math.round(origin.cx - size / 2)}px`;
  ghost.style.top = `${Math.round(origin.cy - size / 2)}px`;
  ghost.style.width = `${size}px`;
  ghost.style.height = `${size}px`;
  document.body.appendChild(ghost);

  const fall = window.innerHeight - origin.cy + 160;   // clear the bottom edge from wherever it left
  const TOSS = 760;

  /* ⛔ THE THROW GOES TOWARDS THE ROOM, NOT ALWAYS LEFT (Ryan 2026-08-09: *"on mobile… if they plop
     up and to the left before falling down, they plop off the screen and it's hard to see the
     animation"*). Desktop's bar is bottom-CENTRE and a left throw looks right there; the phone's is
     top-LEFT, where the same throw leaves the viewport before the fall even starts.
       Derived from the origin rather than branched on `isPhone()`, because the thing that decides it
     is where the bar happens to be — which is a layout fact, not a platform one. A bar in the left
     half throws right, anything else throws left; dead centre resolves to left, which is desktop's
     existing behaviour and the one Ryan has already approved.
     ⚠ The travel is CLAMPED to the room on that side too, so a narrow phone gets a shorter arc
     instead of the same 150px hurled past its own edge — and the tumble mirrors with it, or the
     plate would rotate into the direction it is leaving. */
  const dir = origin.cx < window.innerWidth / 2 ? 1 : -1;
  const room = dir > 0 ? window.innerWidth - origin.cx : origin.cx;
  const apexX = dir * Math.min(86, Math.max(24, room * 0.45));
  const endX = dir * Math.min(150, Math.max(40, room * 0.78));
  const spin = -dir;   // tumbles away from the direction of travel

  /* ⚠ TWO EASINGS, NOT ONE, because a single curve reads as a slide rather than a throw. The rise is
     decelerating (a toss reaching its apex) and the fall is accelerating (gravity taking over) —
     that is the whole difference between "thrown over a railing" and "moved to the corner". */
  const anim = ghost.animate(
    [
      { transform: 'translate(0px, 0px) rotate(0deg) scale(1)', opacity: 0.95, easing: 'cubic-bezier(0.2, 0.7, 0.4, 1)', offset: 0 },
      { transform: `translate(${Math.round(apexX)}px, -78px) rotate(${spin * 16}deg) scale(0.96)`, opacity: 0.95, easing: 'cubic-bezier(0.5, 0, 0.9, 0.6)', offset: 0.3 },
      { transform: `translate(${Math.round(endX)}px, ${Math.round(fall)}px) rotate(${spin * 104}deg) scale(0.82)`, opacity: 0.25, offset: 1 },
    ],
    { duration: TOSS, fill: 'forwards' },
  );
  const done = () => ghost.remove();
  anim.finished.then(done, done);
  setTimeout(done, TOSS + 400);
}
